import { useContext, useState } from 'react';
import {
  SidebarMenuItem,
  Popover,
  Button,
  IconName,
  Icon,
  Tooltip,
  Separator,
  ButtonVariant,
  Text,
  TextField,
  Box,
} from '@usealloy/component-library';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '~/dashboard/context';
import type { UserContextValue } from '~/dashboard/context';
import { routes } from '~/helpers/routes';
import styled from 'styled-components';
import { RbacCountdown } from './RbacCountdown';

// Types

interface CustomerSwitcherProps {
  expanded?: boolean;
}

interface StyledSidebarMenuItemProps {
  warn?: boolean;
  expanded?: boolean;
}

// Utils

const getInitials = (name: string) => name
  .split(/\s+/)
  .map(word => word.replaceAll(/[^a-z]/gi, '').charAt(0))
  .filter(Boolean)
  .slice(0, 4)
  .join('')
  .toUpperCase();

const getOrgsToDisplay = ({
  customer,
  customerAgents,
}: {
  customer: UserContextValue['customer'];
  customerAgents?: Array<{
    customerId?: number;
    customerAgentId: number;
    organizationName: string;
    organizationNameSlug: string;
    primaryAccount: boolean;
    customerAgentActive: boolean;
    archived?: boolean;
  }>;
}) => {
  const currentOrg = [];
  const otherAvailableOrgItems = [];

  for (const ca of customerAgents ?? []) {
    const org = {
      orgId: ca.customerId,
      orgName: ca.organizationName,
      orgNameSlug: ca.organizationNameSlug,
      customerAgentId: ca.customerAgentId,
      primaryAccount: ca.primaryAccount,
    };

    if (ca.customerId === customer.customerId) {
      currentOrg.push(org);
    } else if (ca.customerAgentActive && !ca.archived) {
      otherAvailableOrgItems.push(org);
    }
  }

  return { currentOrg: currentOrg.at(0), otherAvailableOrgItems };
};

// Internal components

const IconContainer = styled('span')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  height: 24,
  width: 24,

  backgroundColor: '#191919', // theme.palette.background.accent in FE-P
  color: '#FAFAF9', // theme.palette.text.accent in FE-P
  borderRadius: '4px', // theme.borderRadius['1'] in FEP-P

  fontSize: '0.75rem', // theme.typography.mixins.body({size: "medium"})
  lineHeight: '1.125rem', // theme.typography.mixins.body({size: "medium"})
  fontWeight: '700', // theme.typography.fontWeight.bold

  '[data-expanded="false"] &': {
    width: '100%',
    transitionDelay: '100ms', // theme.animation.duration.shortest
  },

  transitionProperty: 'width',
  transitionDuration: '300ms', // theme.animation.duration.standard
  transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)', // theme.animation.motion["ease-in-out"]

  svg: {
    color: '#FAFAF9',
    height: 16,
    width: 16,
  },
});

const OrgNameText = styled(Text)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
  flex: 1,
});

const StyledSidebarMenuItem = styled(
  SidebarMenuItem,
)<StyledSidebarMenuItemProps>`
  width: 100%;
  --bg-color: ${props => (props.warn ? '#FBA6A7' : '#EBEBE6')};
  --bg-color-hover: ${props => (props.warn
    ? 'oklch(from #FBA6A7 calc(l * .9) c h)'
    : 'oklch(from #EBEBE6 calc(l * .9) c h)')};
  border-radius: 0;
  display: flex;
  flex-direction: row;
  height: 48px;
  ${props => props.expanded && { 'padding-inline': 'calc(16px + 12px)' }}
  padding-inline: 16px;
`;

const SwitchableAgentButton = styled(Button)({
  width: '100%',
  justifyContent: 'flex-start',
  background: 'none',
  border: 'none',
  boxShadow: 'none',
  padding: '6px 12px',
  fontWeight: 400,
  borderRadius: 0,
  '&:hover': {
    background: 'oklch(from #EBEBE6 calc(l * .95) c h)',
  },
});

const SectionLabel = styled(Text)({
  padding: '8px 12px 4px',
  color: '#646461',
});

const CurrentOrgItem = styled(Text)({
  width: '100%',
  padding: '8px 12px',
});

// CustomerSwitcher

export const CustomerSwitcher = ({ expanded }: CustomerSwitcherProps) => {
  const {
    agent,
    customer,
    adminCustomerAccess,
    adminPermissions,
    disableCustomerPicker,
  } = useContext(UserContext);
  const [searchQuery, setSearchQuery] = useState('');

  const location = useLocation();
  const navigate = useNavigate();

  const inAdminDashboard = location.pathname.startsWith('/v3/dashboard/admin');
  const shouldWarn = agent?.isEmployee
    && customer?.parentOrganizationSlug?.toLowerCase() !== 'alloy';

  const isInCustomerAccount = customer?.customerAgentId === null;

  const rbacAccess = customer?.organizationNameSlug && adminCustomerAccess
    ? adminCustomerAccess[customer.organizationNameSlug]
    : undefined;
  const rbacExpirationTimestamp = rbacAccess?.expiredAt
    ? new Date(rbacAccess.expiredAt).getTime()
    : 0;
  const shouldShowExpirationTimestamp = isInCustomerAccount
    && rbacExpirationTimestamp !== 0
    && !adminPermissions?.['READ:Customers:null']
    && !rbacAccess?.isPermanent;

  const getOrganizationName = () => {
    if (inAdminDashboard) {
      return agent?.isEmployee
        ? 'Alloy Internal Dashboard'
        : customer?.organizationName
            || agent?.customerAgents?.[0]?.organizationName;
    }
    return customer?.organizationName;
  };

  const { currentOrg, otherAvailableOrgItems } = getOrgsToDisplay({
    customer: customer ?? {},
    customerAgents: agent?.customerAgents,
  });

  const orgName = getOrganizationName() || '';
  const orgInitials = getInitials(customer?.organizationName ?? '');

  const CustomerSwitcherLabel = (
    <>
      <Tooltip disabled={expanded} content={orgName} side="right">
        <IconContainer>
          {expanded ? <Icon icon={IconName.Alloy} /> : orgInitials}
        </IconContainer>
      </Tooltip>
      <OrgNameText id="current-organization-name">
        {customer?.organizationName}
      </OrgNameText>

      {!!shouldShowExpirationTimestamp && (
        <RbacCountdown expiredAt={rbacExpirationTimestamp} />
      )}
      {!disableCustomerPicker && <Icon icon={IconName.CaretRight} />}
    </>
  );

  const filteredOrgItems = otherAvailableOrgItems.filter(org => (searchQuery
    ? org.orgName.toLowerCase().includes(searchQuery.toLowerCase())
    : true),
  );

  return !disableCustomerPicker ? (
    <Popover
      trigger={(
        <StyledSidebarMenuItem warn={shouldWarn} expanded={expanded}>
          {CustomerSwitcherLabel}
        </StyledSidebarMenuItem>
      )}
      side="right"
      align="start"
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        minWidth: '250px',
      }}
    >
      <div style={{ padding: '8px 8px 4px' }}>
        <TextField
          type="text"
          leadingIcon={IconName.Search}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search..."
          value={searchQuery}
          style={{ width: '100%' }}
        />
      </div>

      {!!currentOrg && searchQuery.length === 0 && (
        <>
          <SectionLabel size={1}>Current</SectionLabel>
          <CurrentOrgItem size={2}>{currentOrg?.orgName}</CurrentOrgItem>
        </>
      )}

      {filteredOrgItems.length > 0 ? (
        <>
          <SectionLabel size={1}>All Customer Accounts</SectionLabel>
          {filteredOrgItems.map(org => (
            <SwitchableAgentButton
              key={`${org.orgId}-${org.customerAgentId}`}
              onClick={() => {
                window.location.href = routes.switchCustomer(org.orgId);
              }}
              variant={ButtonVariant.tertiary}
            >
              {`${org.orgName}${org.primaryAccount ? ' (primary)' : ''}`}
            </SwitchableAgentButton>
          ))}
        </>
      ) : (
        searchQuery && (
          <Box style={{ minHeight: '64px' }}>
            <Text
              size={2}
              style={{
                padding: '8px 12px',
                color: '#6B6B60',
              }}
            >
              No results
            </Text>
          </Box>
        )
      )}

      {otherAvailableOrgItems.length > 0 && <Separator />}
      <div style={{ padding: '8px' }}>
        <Button
          icon={IconName.ArrowLeft}
          onClick={() => {
            navigate(routes.v3.dashboard.admin.customers.list);
          }}
          style={{ width: '100%' }}
          variant={ButtonVariant.tertiary}
        >
          Customer List
        </Button>
      </div>
    </Popover>
  ) : (
    <StyledSidebarMenuItem warn={shouldWarn}>
      {CustomerSwitcherLabel}
    </StyledSidebarMenuItem>
  );
};
